import { Grid, Box, Typography, Alert, Divider } from "@mui/material";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from 'yup';
import { FormProvider, RHFFileField } from "../../common/hook-form";
import { LoadingButton } from "@mui/lab";
import { Upload as UploadIcon, CloudUpload as CloudUploadIcon, Description as FileIcon } from '@mui/icons-material';
import { useSnackbar } from "notistack";
import { apiConstants, postMultipart } from "../../common/constants";
import DownloadSampleFile from "../../components/DownloadSampleFile";

export default function HolidayListUpload({ onSuccess }) {
    const [isUploading, setIsUploading] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const uploadMethods = useForm({
        resolver: yupResolver(Yup.object().shape({
            file: Yup.mixed().required("Please select a file"),
        })),
    });
    const { handleSubmit, reset } = uploadMethods;

    const onSubmit = async (data) => {
        setIsUploading(true);
        try {
            let formData = new FormData();
            formData.append("excelFile", data?.file[0]);
            const response = await postMultipart(apiConstants.UPLOAD_BULK_EDDHOLIDAYS, formData);
            if (response.status === 200) {
                enqueueSnackbar("Holiday list uploaded successfully!", { variant: 'success' });
                reset();
                if (onSuccess) onSuccess();
            }
        } catch (error) {
            console.error("Error uploading holiday list:", error);
            const errorMessage = error?.response?.data?.message || error?.message || "Failed to upload holiday list";
            enqueueSnackbar(errorMessage, { variant: 'error' });
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <Box>
            <Alert severity="info" variant="outlined" sx={{ mb: 3, '& .MuiAlert-message': { fontSize: 13 } }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Bulk Upload Holidays</Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                    Upload a CSV or Excel file to add multiple holidays at once.
                    Download the sample file below to see the required format.
                </Typography>
            </Alert>

            {/* Sample File Download */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, border: '1px dashed #ccc', borderRadius: 2, mb: 3, backgroundColor: '#fafafa' }}>
                <FileIcon color="action" />
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">Sample Template</Typography>
                    <Typography variant="caption" color="text.secondary">
                        Download and fill in the required fields
                    </Typography>
                </Box>
                <DownloadSampleFile
                    fileName={"EDD_HOLIDAY_SAMPLE_PUSH"}
                    label='Download Sample'
                    excel={'excel'}
                    variant="outlined"
                    size="small"
                />
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Upload Section */}
            <FormProvider methods={uploadMethods} onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{ p: 3, border: '2px dashed #e0e0e0', borderRadius: 2, textAlign: 'center', backgroundColor: '#fafafa', mb: 3 }}>
                    <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Choose your file</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                        Supported formats: CSV, XLS, XLSX
                    </Typography>
                    <RHFFileField name='file' />
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <LoadingButton
                            loading={isUploading}
                            variant="contained"
                            color="warning"
                            startIcon={<UploadIcon />}
                            size="medium"
                            type="submit"
                            fullWidth
                        >
                            Upload Holiday List
                        </LoadingButton>
                    </Grid>
                </Grid>
            </FormProvider>
        </Box>
    );
}
